import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { ContractCard } from "@/pages/contractSigning/ContractCard";

type SignableProduct = InterludeTypes["SignableProduct"];
type ContractType = InterludeTypes["ContractType"];

// MSA on the left, addendum on the right.
const CONTRACT_TYPE_ORDER: Record<ContractType, number> = {
  msa: 0,
  addendum: 1,
};

// Hosts a return_url may point back to. The server validates the redirect it
// embeds, but a hand-crafted /contract-signing link reaches the client
// directly, so re-check here before navigating.
const ALLOWED_RETURN_URL_SUFFIX = ".cresclab.com";

function isSafeReturnUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  // https only — an http return_url can be tampered with in transit.
  if (parsed.protocol !== "https:") return false;
  const host = parsed.hostname.toLowerCase();
  return host === "cresclab.com" || host.endsWith(ALLOWED_RETURN_URL_SUFFIX);
}

const styles = {
  centered: {
    display: "flex",
    justifyContent: "center",
    py: "48px",
  },
  contractsRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
    alignItems: "stretch",
    mt: "16px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    mt: "24px",
  },
  doneBox: {
    p: "32px",
    textAlign: "center",
  },
  returnButtonBox: {
    mt: "16px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface SigningFlowProps {
  /** Signing is always single-product: the customer arrives via a product
   *  redirect that carries the product. The MSA (if unsigned) rides along. */
  product: SignableProduct;
  returnUrl?: string | undefined;
}

const SigningFlow: React.FC<SigningFlowProps> = function SigningFlow({
  product,
  returnUrl,
}) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [isAgreed, setIsAgreed] = useState(false);

  const toSignQuery = interlude.contract.useGetToSign({
    queries: { product },
  });

  // No onError: a failed agree is reported once by the global handler (see
  // handleError.ts), and React Query clears isPending on its own, so the button
  // re-enables for a retry without any local state.
  const agreeMutation = interlude.contract.useAgree(
    {},
    {
      onSuccess() {
        setIsAgreed(true);
      },
    },
  );

  const contracts = useMemo(() => {
    const list = toSignQuery.data?.contracts ?? [];
    return list.toSorted(
      (a, b) => CONTRACT_TYPE_ORDER[a.type] - CONTRACT_TYPE_ORDER[b.type],
    );
  }, [toSignQuery.data]);

  const allChecked =
    contracts.length > 0 && contracts.every((c) => checked[c.versionKey]);

  const safeReturnUrl =
    returnUrl && isSafeReturnUrl(returnUrl) ? returnUrl : undefined;

  const handleAgree = useHandler(() => {
    agreeMutation.mutate({
      product,
      versionKeys: contracts.map((c) => c.versionKey),
    });
  });

  const handleToggle = useHandler(
    (versionKey: string, checkedValue: boolean) => {
      setChecked((prev) => ({ ...prev, [versionKey]: checkedValue }));
    },
  );

  const handleReturnToApp = useHandler(() => {
    // Only ever the already-validated first-party URL; the button is not
    // rendered without one.
    if (safeReturnUrl) {
      window.location.href = safeReturnUrl;
    }
  });

  // isPending, not isLoading: v5 derives isLoading as isPending && isFetching,
  // so a paused request (offline) would skip the spinner and fall through to
  // "nothing to sign" — the one message this page must not show wrongly.
  if (toSignQuery.isPending) {
    return (
      <Box sx={styles.centered}>
        <CircularProgress />
      </Box>
    );
  }

  if (toSignQuery.isError) {
    return (
      <InfoBox
        variant="error"
        description={t(
          "contractSigning.error.load",
          "Contract signing is not available right now. Please try again later.",
        )}
      />
    );
  }

  if (isAgreed || contracts.length === 0) {
    return (
      <Paper sx={styles.doneBox}>
        <Typography variant="h2">
          {isAgreed
            ? t(
                "contractSigning.success",
                "Thank you! The contracts have been signed.",
              )
            : t(
                "contractSigning.empty",
                "There are no contracts to sign right now.",
              )}
        </Typography>
        {safeReturnUrl && (
          <Box sx={styles.returnButtonBox}>
            <Button variant="primary" onClick={handleReturnToApp}>
              {t("contractSigning.backToApp", "Back to the app")}
            </Button>
          </Box>
        )}
      </Paper>
    );
  }

  return (
    <>
      <Box sx={styles.contractsRow}>
        {contracts.map((contract) => (
          <ContractCard
            key={contract.versionKey}
            contract={contract}
            isChecked={checked[contract.versionKey] ?? false}
            onToggle={handleToggle}
          />
        ))}
      </Box>

      <Box sx={styles.footer}>
        <Button
          variant="primary"
          size="large"
          disabled={!allChecked || agreeMutation.isPending}
          onClick={handleAgree}
        >
          {agreeMutation.isPending
            ? t("contractSigning.agreeButtonPending", "Signing…")
            : t("contractSigning.agreeButton", "Agree and continue")}
        </Button>
      </Box>
    </>
  );
};

export { SigningFlow };
export type { SignableProduct };
